import React from 'react';
import type { CmsBlock } from '../../types';
import {
  ChevronUp,
  ChevronDown,
  Trash2,
  Copy,
  Heading,
  AlignLeft,
  Image as ImageIcon,
  List,
  AlertCircle,
  Table as TableIcon,
  Minus,
  Code,
  Columns as ColumnsIcon,
} from 'lucide-react';

interface BlockItemProps {
  block: CmsBlock;
  index: number;
  totalBlocks: number;
  onUpdate: (data: Record<string, any>) => void;
  onTypeChange: (type: CmsBlock['type']) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

export const BlockItem: React.FC<BlockItemProps> = ({
  block,
  index,
  totalBlocks,
  onUpdate,
  onTypeChange,
  onMoveUp,
  onMoveDown,
  onDuplicate,
  onDelete,
}) => {
  const renderBlockEditor = () => {
    switch (block.type) {
      case 'header': {
        const level = block.data.level || 2;
        return (
          <div className="d-flex flex-column gap-2">
            <div className="d-flex align-items-center gap-2">
              <label className="form-label mb-0 small fw-semibold">Heading Level:</label>
              <div className="btn-group btn-group-sm" role="group">
                {[1, 2, 3, 4, 5, 6].map((lvl) => (
                  <button
                    key={lvl}
                    type="button"
                    className={`btn ${level === lvl ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => onUpdate({ ...block.data, level: lvl })}
                  >
                    H{lvl}
                  </button>
                ))}
              </div>
            </div>
            <input
              type="text"
              className="form-control form-control-lg fw-bold"
              placeholder="Heading text..."
              value={block.data.text || ''}
              onChange={(e) => onUpdate({ ...block.data, text: e.target.value })}
            />
          </div>
        );
      }

      case 'paragraph': {
        return (
          <div className="d-flex flex-column gap-2">
            <div className="d-flex gap-1 mb-1">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                title="Bold"
                onClick={() => {
                  const text = block.data.text || '';
                  onUpdate({ ...block.data, text: `${text} <b>bold text</b> ` });
                }}
              >
                <b>B</b>
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                title="Italic"
                onClick={() => {
                  const text = block.data.text || '';
                  onUpdate({ ...block.data, text: `${text} <i>italic text</i> ` });
                }}
              >
                <i>I</i>
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                title="Underline"
                onClick={() => {
                  const text = block.data.text || '';
                  onUpdate({ ...block.data, text: `${text} <u>underline</u> ` });
                }}
              >
                <u>U</u>
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                title="Link"
                onClick={() => {
                  const url = prompt('Enter link URL (e.g. /items or https://...):');
                  if (url) {
                    const text = block.data.text || '';
                    onUpdate({ ...block.data, text: `${text} <a href="${url}">link text</a> ` });
                  }
                }}
              >
                🔗 Link
              </button>
            </div>
            <textarea
              className="form-control"
              rows={4}
              placeholder="Write rich paragraph text (supports HTML tags like <b>, <i>, <a>)..."
              value={block.data.text || ''}
              onChange={(e) => onUpdate({ ...block.data, text: e.target.value })}
            />
          </div>
        );
      }

      case 'image': {
        return (
          <div className="d-flex flex-column gap-2">
            <div className="row g-2">
              <div className="col-md-7">
                <label className="form-label small fw-semibold">Image URL:</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="/img/maps/... or https://..."
                  value={block.data.url || (block.data.file?.url) || ''}
                  onChange={(e) => onUpdate({ ...block.data, url: e.target.value })}
                />
              </div>
              <div className="col-md-5">
                <label className="form-label small fw-semibold">Caption:</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Image caption..."
                  value={block.data.caption || ''}
                  onChange={(e) => onUpdate({ ...block.data, caption: e.target.value })}
                />
              </div>
            </div>
            {(block.data.url || block.data.file?.url) && (
              <div className="mt-2 text-center p-2 border rounded bg-body-tertiary">
                <img
                  src={block.data.url || block.data.file?.url}
                  alt={block.data.caption || 'Preview'}
                  className="img-fluid rounded max-h-300"
                  onError={(e) => ((e.target as HTMLElement).style.display = 'none')}
                />
                {block.data.caption && <div className="small text-secondary mt-1">{block.data.caption}</div>}
              </div>
            )}
          </div>
        );
      }

      case 'list': {
        const style = block.data.style || 'unordered';
        const items: string[] = Array.isArray(block.data.items) ? block.data.items : ['First item'];

        const updateItem = (itemIdx: number, val: string) => {
          const next = [...items];
          next[itemIdx] = val;
          onUpdate({ ...block.data, items: next });
        };

        const addItem = () => {
          onUpdate({ ...block.data, items: [...items, ''] });
        };

        const removeItem = (itemIdx: number) => {
          const next = items.filter((_, i) => i !== itemIdx);
          onUpdate({ ...block.data, items: next.length > 0 ? next : [''] });
        };

        return (
          <div className="d-flex flex-column gap-2">
            <div className="d-flex align-items-center gap-2 mb-2">
              <label className="form-label mb-0 small fw-semibold">List Style:</label>
              <div className="btn-group btn-group-sm">
                <button
                  type="button"
                  className={`btn ${style === 'unordered' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => onUpdate({ ...block.data, style: 'unordered' })}
                >
                  Bulleted (•)
                </button>
                <button
                  type="button"
                  className={`btn ${style === 'ordered' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => onUpdate({ ...block.data, style: 'ordered' })}
                >
                  Numbered (1, 2, 3)
                </button>
              </div>
            </div>

            <div className="d-flex flex-column gap-2">
              {items.map((item, idx) => (
                <div key={idx} className="d-flex align-items-center gap-2">
                  <span className="text-secondary small">{style === 'ordered' ? `${idx + 1}.` : '•'}</span>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder={`Item ${idx + 1}...`}
                    value={item}
                    onChange={(e) => updateItem(idx, e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm p-1"
                    onClick={() => removeItem(idx)}
                    title="Remove item"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>

            <button type="button" className="btn btn-outline-primary btn-sm align-self-start mt-1" onClick={addItem}>
              + Add Item
            </button>
          </div>
        );
      }

      case 'callout': {
        const variant = block.data.variant || 'info';
        return (
          <div className="d-flex flex-column gap-2">
            <div className="d-flex align-items-center gap-2">
              <label className="form-label mb-0 small fw-semibold">Callout Style:</label>
              <div className="btn-group btn-group-sm">
                {['info', 'success', 'warning', 'danger'].map((v) => (
                  <button
                    key={v}
                    type="button"
                    className={`btn ${variant === v ? `btn-${v}` : 'btn-outline-secondary'}`}
                    onClick={() => onUpdate({ ...block.data, variant: v })}
                  >
                    {v.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
            <input
              type="text"
              className="form-control fw-semibold"
              placeholder="Callout title (optional)..."
              value={block.data.title || ''}
              onChange={(e) => onUpdate({ ...block.data, title: e.target.value })}
            />
            <textarea
              className="form-control"
              rows={2}
              placeholder="Callout message..."
              value={block.data.text || ''}
              onChange={(e) => onUpdate({ ...block.data, text: e.target.value })}
            />
          </div>
        );
      }

      case 'table': {
        const rows: string[][] = Array.isArray(block.data.content)
          ? block.data.content
          : [
              ['Header 1', 'Header 2'],
              ['Cell 1', 'Cell 2'],
            ];

        const updateCell = (rIdx: number, cIdx: number, val: string) => {
          const next = rows.map((r, ri) => (ri === rIdx ? r.map((c, ci) => (ci === cIdx ? val : c)) : [...r]));
          onUpdate({ ...block.data, content: next });
        };

        const addRow = () => {
          const colCount = rows[0]?.length || 2;
          onUpdate({ ...block.data, content: [...rows, new Array(colCount).fill('')] });
        };

        const addCol = () => {
          const next = rows.map((r) => [...r, '']);
          onUpdate({ ...block.data, content: next });
        };

        return (
          <div className="d-flex flex-column gap-2 overflow-x-auto">
            <div className="d-flex gap-2 mb-2">
              <button type="button" className="btn btn-outline-primary btn-sm" onClick={addRow}>+ Add Row</button>
              <button type="button" className="btn btn-outline-primary btn-sm" onClick={addCol}>+ Add Column</button>
            </div>
            <table className="table table-bordered table-sm mb-0">
              <tbody>
                {rows.map((row, rIdx) => (
                  <tr key={rIdx}>
                    {row.map((cell, cIdx) => (
                      <td key={cIdx} className={rIdx === 0 ? 'bg-body-secondary' : ''}>
                        <input
                          type="text"
                          className="form-control form-control-sm border-0 bg-transparent"
                          value={cell}
                          placeholder={rIdx === 0 ? `Header ${cIdx + 1}` : `Cell`}
                          onChange={(e) => updateCell(rIdx, cIdx, e.target.value)}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }

      case 'columns': {
        const cols: string[] = Array.isArray(block.data.cols) ? block.data.cols : ['', ''];
        return (
          <div className="row g-2">
            {cols.map((colText, idx) => (
              <div key={idx} className={cols.length === 3 ? 'col-md-4' : 'col-md-6'}>
                <label className="form-label small fw-semibold">Column {idx + 1}:</label>
                <textarea
                  className="form-control"
                  rows={4}
                  placeholder={`Column ${idx + 1} content...`}
                  value={colText}
                  onChange={(e) => {
                    const next = [...cols];
                    next[idx] = e.target.value;
                    onUpdate({ ...block.data, cols: next });
                  }}
                />
              </div>
            ))}
          </div>
        );
      }

      case 'code': {
        return (
          <div className="d-flex flex-column gap-2">
            <label className="form-label small fw-semibold">Raw HTML / Code Snippet:</label>
            <textarea
              className="form-control font-monospace"
              rows={4}
              placeholder="<div>Custom HTML or code...</div>"
              value={block.data.html || block.data.code || ''}
              onChange={(e) => onUpdate({ ...block.data, html: e.target.value })}
            />
          </div>
        );
      }

      case 'delimiter':
      default: {
        return (
          <div className="py-2 text-center text-secondary">
            <hr className="my-2" />
            <small>Horizontal Section Divider</small>
          </div>
        );
      }
    }
  };

  const getBlockIcon = () => {
    switch (block.type) {
      case 'header': return <Heading size={14} className="text-primary" />;
      case 'paragraph': return <AlignLeft size={14} className="text-info" />;
      case 'image': return <ImageIcon size={14} className="text-success" />;
      case 'list': return <List size={14} className="text-warning" />;
      case 'callout': return <AlertCircle size={14} className="text-danger" />;
      case 'table': return <TableIcon size={14} className="text-primary" />;
      case 'columns': return <ColumnsIcon size={14} className="text-info" />;
      case 'code': return <Code size={14} className="text-secondary" />;
      default: return <Minus size={14} className="text-secondary" />;
    }
  };

  return (
    <div className="card shadow-sm border mb-3 cms-block-card">
      <div className="card-header bg-body-tertiary p-2 px-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2">
          <span className="badge bg-body border text-body d-flex align-items-center gap-1">
            {getBlockIcon()}
            <span className="text-capitalize">{block.type}</span>
          </span>
          <span className="small text-secondary">#{index + 1}</span>
        </div>

        <div className="d-flex align-items-center gap-1">
          {/* Type Selector Dropdown */}
          <select
            className="form-select form-select-sm w-auto py-0 px-2 small"
            value={block.type}
            onChange={(e) => onTypeChange(e.target.value as CmsBlock['type'])}
          >
            <option value="header">Heading</option>
            <option value="paragraph">Paragraph</option>
            <option value="image">Image</option>
            <option value="list">List</option>
            <option value="callout">Callout</option>
            <option value="table">Table</option>
            <option value="columns">Columns</option>
            <option value="code">Raw HTML</option>
            <option value="delimiter">Divider</option>
          </select>

          {/* Reordering Controls */}
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm p-1"
            disabled={index === 0}
            onClick={onMoveUp}
            title="Move Up"
          >
            <ChevronUp size={16} />
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm p-1"
            disabled={index === totalBlocks - 1}
            onClick={onMoveDown}
            title="Move Down"
          >
            <ChevronDown size={16} />
          </button>

          {/* Duplicate Block */}
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm p-1"
            onClick={onDuplicate}
            title="Duplicate Block"
          >
            <Copy size={14} />
          </button>

          {/* Delete Block */}
          <button
            type="button"
            className="btn btn-outline-danger btn-sm p-1"
            onClick={onDelete}
            title="Delete Block"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      <div className="card-body p-3">
        {renderBlockEditor()}
      </div>
    </div>
  );
};
